import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { Link } from 'react-router-dom'
import { withMarkers } from 'scenes/LiveUpdates/components/withMarkers'
import Illustration from './Illustration'

const t = buildTranslate('live_updates.disabled_banner')

const LEAD_MARKER = '[[lead]]'
const LINK_MARKER = '[[link]]'

// Omitting `resume` renders the resume sentence as plain text.
export type LiveUpdatesDisabledBannerResume =
  | { kind: 'link'; to: string }
  | { kind: 'button'; onClick: () => void }

type Props = {
  // `[[lead]]` in `body` is replaced by `lead`, in bold.
  body: string
  lead?: string
  // `[[link]]` in `resumeText` is replaced by the resume action.
  resumeText: string
  resume?: LiveUpdatesDisabledBannerResume
  compact?: boolean
  // Compact mounts sit under an h4 or lower, so they pass a lower level.
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  testId?: string
}

const BANNER_RADIUS = '8px'
const BANNER_GAP = '16px'
const TEXT_GAP = '4px'
// Inline link-looking minimal Button. Longhand styles only (Baseweb overrides
// drop shorthand). Every state is spelled out: uikit spreads consumer
// overrides over its own slot style, so an undeclared state falls through to
// the Baseweb primary treatment (see FeedbackInbox/textLinkButtonOverrides).
//
// Focus: baseui paints an INSET 3px ring on `$isFocusVisible`, which for an
// inline text button sits over the glyphs. Replace it with an outline offset
// outside the text, drawn only on keyboard focus.
type BaseButtonStyleProps = { $isFocusVisible?: boolean }
const resumeButtonOverrides = {
  BaseButton: {
    style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
      display: 'inline',
      paddingTop: '0px',
      paddingRight: '0px',
      paddingBottom: '0px',
      paddingLeft: '0px',
      backgroundColor: 'transparent',
      borderTopWidth: '0px',
      borderRightWidth: '0px',
      borderBottomWidth: '0px',
      borderLeftWidth: '0px',
      borderTopStyle: 'none' as const,
      borderRightStyle: 'none' as const,
      borderBottomStyle: 'none' as const,
      borderLeftStyle: 'none' as const,
      color: palette.belizeHole,
      fontFamily: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      fontWeight: 700,
      textDecorationLine: 'underline',
      cursor: 'pointer',
      boxShadow: 'none',
      outlineStyle: $isFocusVisible ? ('solid' as const) : ('none' as const),
      outlineWidth: '2px',
      outlineOffset: '2px',
      outlineColor: palette.belizeHole,
      ':hover': { backgroundColor: 'transparent' },
      ':active': { backgroundColor: 'transparent' },
    }),
  },
}

const LiveUpdatesDisabledBanner = ({
  body,
  lead,
  resumeText,
  resume,
  compact = false,
  headingTag = 'h2',
  testId = 'live-updates-disabled-banner',
}: Props) => {
  const [css] = useStyletron()
  const linkLabel = t('resume_link')

  const renderBody = () => {
    if (lead === undefined) return body
    return withMarkers(body, [
      [
        LEAD_MARKER,
        <b className={css({ fontWeight: 700, color: palette.grey90 })}>
          {lead}
        </b>,
      ],
    ])
  }

  const renderResumeAction = () => {
    if (!resume) return linkLabel
    if (resume.kind === 'link') {
      return (
        <Link
          to={resume.to}
          className={css({
            color: palette.belizeHole,
            fontWeight: 700,
            textDecorationLine: 'underline',
          })}
        >
          {linkLabel}
        </Link>
      )
    }
    return (
      <Button
        kind="minimal"
        onClick={resume.onClick}
        overrides={resumeButtonOverrides}
      >
        {linkLabel}
      </Button>
    )
  }

  return (
    <div
      role="status"
      data-testid={testId}
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: BANNER_GAP,
        paddingTop: compact ? '12px' : '16px',
        paddingBottom: compact ? '12px' : '16px',
        paddingLeft: compact ? '16px' : '24px',
        paddingRight: compact ? '16px' : '24px',
        borderRadius: BANNER_RADIUS,
        borderTopWidth: '1px',
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderTopColor: palette.grey20,
        borderRightColor: palette.grey20,
        borderBottomColor: palette.grey20,
        borderLeftColor: palette.grey20,
        backgroundColor: palette.grey10,
      })}
    >
      {!compact && <Illustration />}
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: TEXT_GAP,
          flex: 1,
          minWidth: 0,
        })}
      >
        <Text.Header4
          tag={headingTag}
          className={css({ marginTop: 0, marginBottom: 0 })}
        >
          {t('title')}
        </Text.Header4>
        <Text.BodyText4 color="grey70" className={css({ display: 'block' })}>
          {renderBody()}
        </Text.BodyText4>
        <Text.BodyText4 color="grey70" className={css({ display: 'block' })}>
          {withMarkers(resumeText, [[LINK_MARKER, renderResumeAction()]])}
        </Text.BodyText4>
      </div>
    </div>
  )
}

export default LiveUpdatesDisabledBanner
